import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _typeof from "@babel/runtime/helpers/typeof";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { fromJS, List, Map } from 'immutable';
// See "Storing compressed data into localStorage" at https://pieroxy.net/blog/pages/lz-string/guide.html
// for info about storing compressed strings in localStorage.
// Short version is that we must use compressToUTF16 and decompressFromUTF16 to make it work in IE, Edge
// and Firefox.
import md5 from 'js-md5';
import { compressToUTF16, decompress, decompressFromUTF16 } from 'lz-string';
import moment from 'moment';
import appConfig from 'app-config';
import { getTelavoxFormattedLanguageCode, isEmptyNullOrUndefined, ISO_8601_MS_RESOLUTION, keyIn } from 'flow/utils';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { clearConsentCookies } from 'flow/utils/cookie-utils';
import { IncomingCallSetting, PlayRingtoneSetting } from 'flow/utils/call-utils';
import { addElectronLogRow, clearElectronLog, getElectronLog } from 'flow/utils/electron-log-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import { MICROPHONE, permissionTypes, probeMediaPermission } from 'flow/utils/media-utils';
import { DisplayMode } from 'flow/utils/rtc-utils';
import { removeSelectedOperator, removeSelectedQueue } from 'flow/utils/supervisor-utils';
import isElectron from 'is-electron';
var AUTOMATICALLY_SEND_CLIENT_LOGS = 'automaticallySendClientLogs';
var RECENT_COLLEAGUES = 'recent';
var LOG = 'log';
export var IDENTITY = 'identity';
var CALL_METHOD = 'callMethod';
export var CALL_CANVAS_SIZE = 'callCanvasSize';
var AUTOANSWER_IP_PHONE = 'autoAnswerWithIpPhone';
export var REGISTER_SOFTPHONE = 'registerSoftphone';
var MAKE_CALLS_ENABLED = 'makeCallsEnabled';
var PLAY_RINGTONE_SETTING = 'playRingtoneSetting';
var DEPARTMENTS_OPEN = 'departmentsOpen';
var PBX_OPEN = 'pbxOpen';
export var INCOMING_CALL_SETTING = 'incomingCallSetting';
export var REDUX_STATE_DEPRECATED = 'reduxState';
export var REDUX_STATE = 'reduxStateUTF16';
var HEADSET_CONF = 'headsetConf';
var PREFERRED_MICROPHONE_ID = 'preferredMicrophoneId';
var PREFERRED_AUDIO_OUTPUT_ID = 'preferredAudioOutputId';
var PREFERRED_RINGTONE_AUDIO_OUTPUT_ID = 'preferredRingtoneAudioOutputId';
var PREFERRED_VIDEO_INPUT_ID = 'preferredVideoInputId';
var NOISE_SUPPRESSION_ENGINE = 'noiseSuppressionEngine';
var PREFERRED_AUDIO_OUTPUT_VOLUME = 'preferredAudioOutputVolume';
var PREFERRED_RINGTONE_VOLUME = 'preferredRingtoneVolume';
var AUDIO_DEVICE_DATA = 'audioDeviceData';
var ADMIN_LOCATION = 'adminLocation';
var CHAT_SHORTCUTS = 'chatShortcuts';
// This install's own copy of whether it is the account's designated companion, so a command
// arriving before the config fetch can be answered synchronously. Home owns the designation
// itself; a copy that outlives the login it was written for is corrected by
// reconcileUndesignatedAccount at boot.
var USE_AS_COMPANION = 'useAsCompanion';
// Whether this surface hands its calls to the desktop app when one is available. Per surface, not
// per user: it describes how the person at this machine is set up right now, so a Teams tab and the
// ordinary web app can answer it differently. See getHandoverToDesktop.
var USE_COMPANION_FOR_CALLS = 'useCompanionForCalls';
var VIDEO_CONFERENCE_WITH_SIP = 'videoConferenceWithSip';
var CALL_WAITING_ENABLED = 'callWaitingEnabled';
var SHOW_LOCAL_VIDEO_IN_GRID = 'showLocalVideoInGrid';
var SHOW_INCOMING_VIDEO = 'showIncomingVideo';
var CENTRAL_SWITCH_WIDGETS = 'centralSwitchWidgets';
var BLIND_TRANSFER_SETTINGS = 'blindTransferSettings';
var USER_HAS_SET_DISPLAY_TICKETS_TAB_TOGGLE_TO_ON = 'userHasSetDisplayTicketsTabToggleToOn';
export var TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY = 'ticketFailedEvents';
var VIDEO_BACKGROUND_PREFERENCES = 'videoBackgroundPreferences';
export var COLISTENING_WIDGET_USERS = 'CoListeningWidgetUsers';
var REDACTED_KEYS = ['email', 'lastname', 'mobile', 'mobileNumber', 'name', 'password', 'personalNumber', 'phone', 'phoneNumber', 'secret', 'ssn', 'surname', 'username'];
var pattern = new RegExp("\"(".concat(REDACTED_KEYS.join('|'), ")\" ?[:=] ?\"(.*?)\""), 'gi');
export var redact = function redact(entry) {
  if (_typeof(entry) === 'object') {
    var clonedEntry = _objectSpread({}, entry);
    Object.entries(clonedEntry).forEach(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 1),
        key = _ref2[0];
      if (REDACTED_KEYS.includes(key)) {
        clonedEntry[key] = 'REDACTED';
      }
    });
    return clonedEntry;
  }
  if (typeof entry !== 'string') return entry;
  return entry.replace(pattern, '"$1":"REDACTED"');
};
var maxLogSizeInBytes = 100000;
export var anyOptionalKeyDown = function anyOptionalKeyDown(e) {
  return e.shiftKey || e.metaKey || e.altKey || e.ctrlKey;
};
export var setInstanceId = function setInstanceId(number) {
  var instanceId = md5(number + '-' + moment().locale('en').format(ISO_8601_MS_RESOLUTION));
  wrappedLocalStorage.setItem('instanceId', instanceId);
};
export var removePersonalInformationIfNewUser = function removePersonalInformationIfNewUser(identifier, newUserCallback) {
  if (!identifier) {
    return;
  }
  if (!wrappedLocalStorage.isSet(IDENTITY)) {
    wrappedLocalStorage.setItem(IDENTITY, JSON.stringify(identifier));
    newUserCallback();
    return;
  }
  var oldIdentity = JSON.parse(wrappedLocalStorage.getItem(IDENTITY));

  // String() guards against a type-only mismatch (e.g. one caller passing a
  // number, another a string for the same identity) falsely reading as an
  // identity switch and wiping consent/logs for no real reason.
  if (String(oldIdentity) !== String(identifier)) {
    clearLog();
    removeSelectedOperator();
    removeSelectedQueue();
    wrappedLocalStorage.removeItem(INCOMING_CALL_SETTING);
    wrappedLocalStorage.removeItem(PLAY_RINGTONE_SETTING);
    wrappedLocalStorage.removeItem(REGISTER_SOFTPHONE);
    wrappedLocalStorage.removeItem(MAKE_CALLS_ENABLED);
    wrappedLocalStorage.removeItem(USER_HAS_SET_DISPLAY_TICKETS_TAB_TOGGLE_TO_ON);
    wrappedLocalStorage.removeItem(TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY);
    wrappedLocalStorage.removeItem(REDUX_STATE);
    wrappedLocalStorage.removeItem(COLISTENING_WIDGET_USERS);
    // The companion settings are equally the previous user's, but cannot be dropped here:
    // the same user logging back in must keep the machine they designated, and only the
    // account's designation says which case this is. Cleared against it at boot instead, in
    // reconcileUndesignatedAccount. Nothing acts on either before that answer arrives, because
    // both companion entry points also require the feature switch, which starts off.
    wrappedLocalStorage.setItem(IDENTITY, JSON.stringify(identifier));
    // Only on an actual identity switch, not first-ever login, so a fresh
    // pre-login consent choice isn't wiped the moment that user logs in.
    clearConsentCookies();
    newUserCallback();
  }
};
export var hasLogs = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var _logs, logs, _t;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          if (!isElectron()) {
            _context.next = 4;
            break;
          }
          _context.prev = 1;
          _context.next = 2;
          return getElectronLog();
        case 2:
          _logs = _context.sent;
          return _context.abrupt("return", !!_logs);
        case 3:
          _context.prev = 3;
          _t = _context["catch"](1);
          return _context.abrupt("return", false);
        case 4:
          logs = wrappedLocalStorage.getItem(LOG);
          return _context.abrupt("return", logs && logs.length > 0);
        case 5:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 3]]);
  }));
  return function hasLogs() {
    return _ref3.apply(this, arguments);
  };
}();
export var getLog = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(shouldClearLog) {
    var log;
    return _regeneratorRuntime.wrap(function (_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          if (!isElectron()) {
            _context2.next = 2;
            break;
          }
          _context2.next = 1;
          return getElectronLog(shouldClearLog);
        case 1:
          return _context2.abrupt("return", _context2.sent);
        case 2:
          log = Promise.resolve(JSON.parse(wrappedLocalStorage.getItem(LOG)));
          if (shouldClearLog) {
            wrappedLocalStorage.removeItem(LOG);
          }
          return _context2.abrupt("return", log);
        case 3:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return function getLog(_x) {
    return _ref4.apply(this, arguments);
  };
}();
export var addTicketLogEntry = function addTicketLogEntry() {
  for (var _len = arguments.length, logRow = new Array(_len), _key = 0; _key < _len; _key++) {
    logRow[_key] = arguments[_key];
  }
  addLogEntry('TICKET', logRow);
};
export var addOmniChannelLogEntry = function addOmniChannelLogEntry() {
  for (var _len2 = arguments.length, logRow = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    logRow[_key2] = arguments[_key2];
  }
  addLogEntry('OMNICHANNEL', logRow);
};
export var addVideoLogEntry = function addVideoLogEntry() {
  for (var _len3 = arguments.length, logRow = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    logRow[_key3] = arguments[_key3];
  }
  addLogEntry('VIDEO', logRow);
};
export var addAudioLogEntry = function addAudioLogEntry() {
  for (var _len4 = arguments.length, logRow = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
    logRow[_key4] = arguments[_key4];
  }
  addLogEntry('AUDIO', logRow);
};
export var addPhoneLogEntry = function addPhoneLogEntry() {
  for (var _len5 = arguments.length, logRow = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
    logRow[_key5] = arguments[_key5];
  }
  addLogEntry('PHONE', logRow);
};
export var addHeadsetLogEntry = function addHeadsetLogEntry() {
  for (var _len6 = arguments.length, logRow = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
    logRow[_key6] = arguments[_key6];
  }
  addLogEntry('HEADSET', logRow);
};
export var addTopLogEntry = function addTopLogEntry() {
  for (var _len7 = arguments.length, logRow = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
    logRow[_key7] = arguments[_key7];
  }
  addLogEntry('TOP', logRow);
};
export var addGeneralLogEntry = function addGeneralLogEntry() {
  for (var _len8 = arguments.length, logRow = new Array(_len8), _key8 = 0; _key8 < _len8; _key8++) {
    logRow[_key8] = arguments[_key8];
  }
  addLogEntry('GENERAL', logRow);
};
export var addMainProcessLogEntry = function addMainProcessLogEntry() {
  for (var _len9 = arguments.length, logRow = new Array(_len9), _key9 = 0; _key9 < _len9; _key9++) {
    logRow[_key9] = arguments[_key9];
  }
  addLogEntry('MAIN', logRow);
};
export var addLocationLogEntry = function addLocationLogEntry() {
  for (var _len0 = arguments.length, logRow = new Array(_len0), _key0 = 0; _key0 < _len0; _key0++) {
    logRow[_key0] = arguments[_key0];
  }
  addLogEntry('LOCATION', logRow);
};
export var addDeviceLogEntry = function addDeviceLogEntry() {
  for (var _len1 = arguments.length, logRow = new Array(_len1), _key1 = 0; _key1 < _len1; _key1++) {
    logRow[_key1] = arguments[_key1];
  }
  addLogEntry('DEVICE', logRow);
};
export var addUserPhoneLogEntry = function addUserPhoneLogEntry() {
  for (var _len10 = arguments.length, logRow = new Array(_len10), _key10 = 0; _key10 < _len10; _key10++) {
    logRow[_key10] = arguments[_key10];
  }
  addLogEntry('USER_PHONE', logRow);
};
export var addChatLogEntry = function addChatLogEntry() {
  for (var _len11 = arguments.length, logRow = new Array(_len11), _key11 = 0; _key11 < _len11; _key11++) {
    logRow[_key11] = arguments[_key11];
  }
  addLogEntry('CHAT', logRow);
};
export var addHomeLogEntry = function addHomeLogEntry() {
  for (var _len12 = arguments.length, logRow = new Array(_len12), _key12 = 0; _key12 < _len12; _key12++) {
    logRow[_key12] = arguments[_key12];
  }
  addLogEntry('HOME', logRow);
};
export var userLogEntry = function userLogEntry() {
  for (var _len13 = arguments.length, logRow = new Array(_len13), _key13 = 0; _key13 < _len13; _key13++) {
    logRow[_key13] = arguments[_key13];
  }
  addLogEntry('USER', logRow);
};
export var addCtiLogEntry = function addCtiLogEntry() {
  for (var _len14 = arguments.length, logRow = new Array(_len14), _key14 = 0; _key14 < _len14; _key14++) {
    logRow[_key14] = arguments[_key14];
  }
  addLogEntry('CTI', logRow);
};
export var addKuandoLogEntry = function addKuandoLogEntry() {
  for (var _len15 = arguments.length, logRow = new Array(_len15), _key15 = 0; _key15 < _len15; _key15++) {
    logRow[_key15] = arguments[_key15];
  }
  addLogEntry('KUANDO', logRow);
};
export var addSupervisorLogEntry = function addSupervisorLogEntry() {
  for (var _len16 = arguments.length, logRow = new Array(_len16), _key16 = 0; _key16 < _len16; _key16++) {
    logRow[_key16] = arguments[_key16];
  }
  addLogEntry('SUPERVISOR', logRow);
};
export var clearLog = function clearLog() {
  if (isElectron()) {
    clearElectronLog();
  } else {
    wrappedLocalStorage.removeItem(LOG);
  }
};
export var addLogEntry = function addLogEntry(logType, logRow) {
  logRow = Array.isArray(logRow) ? logRow.map(redact) : redact(logRow);
  var log = logRow.map(function (log) {
    return _typeof(log) === 'object' ? JSON.stringify(log, null, 2) : log;
  }).join('');
  var logWithType = "[".concat(logType, "] ").concat(log);
  if (appConfig.dev && logType !== 'PHONE') {
    var _console;
    (_console = console).info.apply(_console, [logType].concat(_toConsumableArray(logRow)));
  }
  var instanceId = getInstanceId();
  if (!instanceId) {
    // If we don't have an instanceId the api call to save the logs will fail
    // so we warn about it in the console and skip the log
    console.error("Instance id is not set. Row of type '".concat(logType, "' not added to log."));
    return;
  }
  var row = {
    instanceId: getInstanceId(),
    version: appConfig.version,
    data: moment().locale('en').format(ISO_8601_MS_RESOLUTION) + ' ' + logWithType + '\n'
  };
  if (isElectron()) {
    addElectronLogRow(row);
    return;
  }
  var hasLog = wrappedLocalStorage.getItem(LOG);
  if (!hasLog) {
    wrappedLocalStorage.setItem(LOG, JSON.stringify([]));
  }
  var rawLog = wrappedLocalStorage.getItem(LOG);

  // WEB-9321 Sometimes we fail to set the log in the storage, and since that error is
  // caught we can end up without a log here. Hence the if-check to avoid a type error.
  if (rawLog) {
    var parsedLog = JSON.parse(rawLog);
    parsedLog.push(row);
    while (isFull(parsedLog)) {
      parsedLog.shift();
    }
    wrappedLocalStorage.setItem(LOG, JSON.stringify(parsedLog));
  }
};
export var getInstanceId = function getInstanceId() {
  return wrappedLocalStorage.getItem('instanceId');
};
export var isFull = function isFull(logArray) {
  return maxLogSizeInBytes - lengthInUtf8Bytes(JSON.stringify(logArray)) < 0;
};
export var lengthInUtf8Bytes = function lengthInUtf8Bytes(str) {
  var encoded = encodeURIComponent(str).match(/%[89ABab]/g);
  return str.length + (encoded ? encoded.length : 0);
};
export var isEdge = function isEdge() {
  return /Edge/.test(navigator.userAgent);
};
export var isChrome = function isChrome() {
  return /Chrome/.test(navigator.userAgent) && !isEdge();
};
export var isChromeOrElectron = function isChromeOrElectron() {
  return isChrome() || isElectron();
};
export var isFirefox = function isFirefox() {
  return /Firefox/.test(navigator.userAgent) && !isEdge();
};
export var isSafari = function isSafari() {
  var browserString = navigator.userAgent.toLowerCase();
  return /safari/.test(browserString) && !/chrome/.test(browserString);
};
export var hasNetworkConnection = function hasNetworkConnection() {
  return navigator.onLine;
};
export var setRecentColleague = function setRecentColleague(extensionKey) {
  var prevState = getRecentColleagues();
  if (browserSupportStorage()) {
    if (isEmptyNullOrUndefined(prevState)) {
      var keys = {
        keyStore: [extensionKey]
      };
      wrappedLocalStorage.setItem(RECENT_COLLEAGUES, JSON.stringify(keys));
    } else {
      saveUserToStore(extensionKey);
    }
  }
  return null;
};
export var getRecentColleagues = function getRecentColleagues() {
  if (browserSupportStorage()) {
    return JSON.parse(wrappedLocalStorage.getItem(RECENT_COLLEAGUES));
  } else {
    return null;
  }
};
var saveUserToStore = function saveUserToStore(extensionKey) {
  var prevState = getRecentColleagues();
  var index = prevState.keyStore.indexOf(extensionKey);
  if (index === -1) {
    //If the user doesn't exist in the list place it first
    prevState.keyStore.unshift(extensionKey);
    if (prevState.keyStore.length > 6) {
      prevState.keyStore.splice(6);
    }
    wrappedLocalStorage.setItem(RECENT_COLLEAGUES, JSON.stringify(prevState));
  } else {
    //If the user exists move it to the top of the list.
    prevState.keyStore.splice(index, 1);
    prevState.keyStore.unshift(extensionKey);
    wrappedLocalStorage.setItem(RECENT_COLLEAGUES, JSON.stringify(prevState));
  }
};
export var saveHeadsetEnabledToStorage = function saveHeadsetEnabledToStorage(extensionKey, value) {
  if (!browserSupportStorage()) {
    return false;
  }
  var headsetConf = wrappedLocalStorage.getItem(HEADSET_CONF) ? JSON.parse(wrappedLocalStorage.getItem(HEADSET_CONF)) : {};
  headsetConf[extensionKey] = value;
  wrappedLocalStorage.setItem(HEADSET_CONF, JSON.stringify(headsetConf));
  return true;
};
export var getHeadsetEnabledFromStorage = function getHeadsetEnabledFromStorage(extensionKey) {
  if (!browserSupportStorage()) {
    return null;
  }
  var headsetConf = wrappedLocalStorage.getItem(HEADSET_CONF) ? JSON.parse(wrappedLocalStorage.getItem(HEADSET_CONF)) : {};
  return headsetConf[extensionKey];
};

// A stored value that is not a JSON object must not throw during render, so it
// counts as nothing stored.
var readOpenStates = function readOpenStates(storageKey) {
  try {
    var openStates = JSON.parse(wrappedLocalStorage.getItem(storageKey));
    return openStates && _typeof(openStates) === 'object' ? openStates : {};
  } catch (_error) {
    return {};
  }
};
export var saveDepartmentState = function saveDepartmentState(department, isOpen) {
  if (browserSupportStorage()) {
    var departmentStates = readOpenStates(DEPARTMENTS_OPEN);
    departmentStates[department] = isOpen;
    wrappedLocalStorage.setItem(DEPARTMENTS_OPEN, JSON.stringify(departmentStates));
  }
};
export var getDepartmentState = function getDepartmentState(department) {
  if (browserSupportStorage()) {
    var departmentStates = readOpenStates(DEPARTMENTS_OPEN);
    return Object.prototype.hasOwnProperty.call(departmentStates, department) ? departmentStates[department] : true;
  }
  return true;
};
export var savePbxState = function savePbxState(department, isOpen) {
  if (browserSupportStorage()) {
    var departmentStates = readOpenStates(PBX_OPEN);
    departmentStates[department] = isOpen;
    wrappedLocalStorage.setItem(PBX_OPEN, JSON.stringify(departmentStates));
  }
};
export var getPbxState = function getPbxState(department) {
  if (browserSupportStorage()) {
    var departmentStates = readOpenStates(PBX_OPEN);
    return Object.prototype.hasOwnProperty.call(departmentStates, department) ? departmentStates[department] : true;
  }
  return true;
};
export var saveQueueStatisticsFavorites = function saveQueueStatisticsFavorites(queueKey, favorites) {
  if (browserSupportStorage()) {
    var queueStatisticsFavourite = wrappedLocalStorage.isSet('queueStatisticsFavourites') ? JSON.parse(wrappedLocalStorage.getItem('queueStatisticsFavourites')) : {};
    queueStatisticsFavourite[queueKey] = favorites;
    wrappedLocalStorage.setItem('queueStatisticsFavourites', JSON.stringify(queueStatisticsFavourite));
  }
};
export var getQueueStatisticsFavorites = function getQueueStatisticsFavorites(queueKey) {
  if (browserSupportStorage()) {
    var queueStatisticsFavourite = wrappedLocalStorage.isSet('queueStatisticsFavourites') ? JSON.parse(wrappedLocalStorage.getItem('queueStatisticsFavourites')) : {};
    return Object.prototype.hasOwnProperty.call(queueStatisticsFavourite, queueKey) ? queueStatisticsFavourite[queueKey] : null;
  }
  return [];
};
export var saveChannelStatisticsFavorites = function saveChannelStatisticsFavorites(channelKey, favorites) {
  if (browserSupportStorage()) {
    var channelStatisticsFavourite = wrappedLocalStorage.isSet('channelStatisticsFavorites') ? JSON.parse(wrappedLocalStorage.getItem('channelStatisticsFavorites')) : {};
    channelStatisticsFavourite[channelKey] = favorites;
    wrappedLocalStorage.setItem('channelStatisticsFavorites', JSON.stringify(channelStatisticsFavourite));
  }
};
export var getChannelStatisticsFavorites = function getChannelStatisticsFavorites(channelKey) {
  if (browserSupportStorage()) {
    var channelStatisticsFavourite = wrappedLocalStorage.isSet('channelStatisticsFavorites') ? JSON.parse(wrappedLocalStorage.getItem('channelStatisticsFavorites')) : {};
    return Object.prototype.hasOwnProperty.call(channelStatisticsFavourite, channelKey) ? channelStatisticsFavourite[channelKey] : null;
  }
  return [];
};
export var getSoftphoneShouldBeRegistered = function getSoftphoneShouldBeRegistered(loggedInWithTempPass) {
  if (loggedInWithTempPass) {
    return false;
  }
  if (!browserSupportStorage()) {
    return false;
  }
  if (wrappedLocalStorage.isSet(REGISTER_SOFTPHONE)) {
    return JSON.parse(wrappedLocalStorage.getItem(REGISTER_SOFTPHONE));
  }
  if (wrappedLocalStorage.isSet(INCOMING_CALL_SETTING)) {
    var incomingCallSetting = IncomingCallSetting.get(wrappedLocalStorage.getItem(INCOMING_CALL_SETTING));
    if (incomingCallSetting === IncomingCallSetting.RING || incomingCallSetting === IncomingCallSetting.SILENT) {
      return true;
    }
  }

  //If Desktop app and no preference is set, and user is not logged in with temporary password,
  //default to true
  if (isElectron()) {
    return !loggedInWithTempPass;
  }
  return !loggedInWithTempPass && IS_UAE_HOST;
};
export var saveSoftphoneShouldBeRegistered = function saveSoftphoneShouldBeRegistered(shouldBeRegistered) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(REGISTER_SOFTPHONE, shouldBeRegistered);
  }
};

// The "Make calls" toggle on this client. Kept apart from REGISTER_SOFTPHONE on purpose: that key
// decides whether the softphone is registered, which is what lets this client receive calls, so
// turning off outgoing calls must not touch it (AC-1902). Defaults to on until the user turns it off.
export var saveMakeCallsEnabled = function saveMakeCallsEnabled(makeCallsEnabled) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(MAKE_CALLS_ENABLED, makeCallsEnabled);
  }
};
export var getMakeCallsEnabled = function getMakeCallsEnabled() {
  if (browserSupportStorage() && wrappedLocalStorage.isSet(MAKE_CALLS_ENABLED)) {
    return JSON.parse(wrappedLocalStorage.getItem(MAKE_CALLS_ENABLED));
  }
  return true;
};
export var isSoftphoneExplicitlyDisabled = function isSoftphoneExplicitlyDisabled() {
  if (!browserSupportStorage()) return false;
  if (wrappedLocalStorage.isSet(REGISTER_SOFTPHONE)) {
    return !JSON.parse(wrappedLocalStorage.getItem(REGISTER_SOFTPHONE));
  }
  return false;
};
export var getPreferredRingtoneVolume = function getPreferredRingtoneVolume() {
  if (browserSupportStorage() && wrappedLocalStorage.isSet(PREFERRED_RINGTONE_VOLUME)) {
    return JSON.parse(wrappedLocalStorage.getItem(PREFERRED_RINGTONE_VOLUME));
  }
  return 1;
};
export var savePreferredRingtoneVolume = function savePreferredRingtoneVolume(volume) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(PREFERRED_RINGTONE_VOLUME, volume);
  }
};
export var getPreferredAudioOutputVolume = function getPreferredAudioOutputVolume() {
  if (browserSupportStorage() && wrappedLocalStorage.isSet(PREFERRED_AUDIO_OUTPUT_VOLUME)) {
    return JSON.parse(wrappedLocalStorage.getItem(PREFERRED_AUDIO_OUTPUT_VOLUME));
  }
  return 1;
};
export var savePreferredAudioOutputVolume = function savePreferredAudioOutputVolume(volume) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(PREFERRED_AUDIO_OUTPUT_VOLUME, volume);
  }
};
export var getPlayRingtoneSetting = function getPlayRingtoneSetting() {
  if (!browserSupportStorage()) {
    return PlayRingtoneSetting.RING;
  }
  if (wrappedLocalStorage.isSet(PLAY_RINGTONE_SETTING)) {
    return PlayRingtoneSetting.get(wrappedLocalStorage.getItem(PLAY_RINGTONE_SETTING));
  }
  if (wrappedLocalStorage.isSet(INCOMING_CALL_SETTING)) {
    var incomingCallSetting = IncomingCallSetting.get(wrappedLocalStorage.getItem(INCOMING_CALL_SETTING));
    if (incomingCallSetting === IncomingCallSetting.SILENT) {
      return PlayRingtoneSetting.SILENT;
    }
  }
  return PlayRingtoneSetting.RING;
};
export var savePlayRingtoneSetting = function savePlayRingtoneSetting(playRingtoneSetting) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(PLAY_RINGTONE_SETTING, playRingtoneSetting.key);
  }
};
export var savePreferredMicrophoneId = function savePreferredMicrophoneId(microphoneId) {
  if (microphoneId && browserSupportStorage()) {
    wrappedLocalStorage.setItem(PREFERRED_MICROPHONE_ID, microphoneId);
  }
};
export var getPreferredMicrophoneId = function getPreferredMicrophoneId() {
  if (!browserSupportStorage()) {
    return undefined;
  }
  if (wrappedLocalStorage.isSet(PREFERRED_MICROPHONE_ID)) {
    return wrappedLocalStorage.getItem(PREFERRED_MICROPHONE_ID);
  }
  return undefined;
};
export var saveNoiseSuppressionEngine = function saveNoiseSuppressionEngine(engine) {
  if (engine && browserSupportStorage()) {
    wrappedLocalStorage.setItem(NOISE_SUPPRESSION_ENGINE, engine);
  }
};
export var getNoiseSuppressionEngine = function getNoiseSuppressionEngine() {
  if (!browserSupportStorage()) {
    return undefined;
  }
  if (wrappedLocalStorage.isSet(NOISE_SUPPRESSION_ENGINE)) {
    return wrappedLocalStorage.getItem(NOISE_SUPPRESSION_ENGINE);
  }
  return undefined;
};
export var savePreferredVideoInputId = function savePreferredVideoInputId(videoInputId) {
  if (videoInputId && browserSupportStorage()) {
    wrappedLocalStorage.setItem(PREFERRED_VIDEO_INPUT_ID, videoInputId);
  }
};
export var getPreferredVideoInputId = function getPreferredVideoInputId() {
  if (!browserSupportStorage()) {
    return undefined;
  }
  if (wrappedLocalStorage.isSet(PREFERRED_VIDEO_INPUT_ID)) {
    return wrappedLocalStorage.getItem(PREFERRED_VIDEO_INPUT_ID);
  }
  return undefined;
};
export var savePreferredAudioOutputId = function savePreferredAudioOutputId(audioOutputId) {
  if (audioOutputId && browserSupportStorage()) {
    wrappedLocalStorage.setItem(PREFERRED_AUDIO_OUTPUT_ID, audioOutputId);
  }
};
export var getPreferredAudioOutputId = function getPreferredAudioOutputId() {
  if (!browserSupportStorage()) {
    return undefined;
  }
  if (wrappedLocalStorage.isSet(PREFERRED_AUDIO_OUTPUT_ID)) {
    return wrappedLocalStorage.getItem(PREFERRED_AUDIO_OUTPUT_ID);
  }
  return undefined;
};
export var saveAudioDeviceDataSentForStatistics = function saveAudioDeviceDataSentForStatistics(data) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(AUDIO_DEVICE_DATA, JSON.stringify(data));
  }
};
export var getAudioDeviceDataSentForStatistics = function getAudioDeviceDataSentForStatistics() {
  if (!browserSupportStorage()) {
    return undefined;
  }
  if (wrappedLocalStorage.isSet(AUDIO_DEVICE_DATA)) {
    var data = wrappedLocalStorage.getItem(AUDIO_DEVICE_DATA);
    return JSON.parse(data);
  }
  return undefined;
};

/**
 * @param audioOutputId saved to storage UNLESS we pass null/undefined. In that case we no longer want
 * a preferred ringtone audio output id to be saved, and thus remove the item from storage.
 */

export var savePreferredRingtoneAudioOutputId = function savePreferredRingtoneAudioOutputId(audioOutputId) {
  if (!browserSupportStorage()) {
    return;
  }
  if (audioOutputId) {
    wrappedLocalStorage.setItem(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID, audioOutputId);
  } else if (wrappedLocalStorage.isSet(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID)) {
    wrappedLocalStorage.removeItem(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID);
  }
};
export var getPreferredRingtoneAudioOutputId = function getPreferredRingtoneAudioOutputId() {
  if (!browserSupportStorage() && !wrappedLocalStorage.isSet(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID)) {
    return undefined;
  }
  return wrappedLocalStorage.getItem(PREFERRED_RINGTONE_AUDIO_OUTPUT_ID);
};
export var saveAutomaticallySendClientLogs = function saveAutomaticallySendClientLogs(enabled) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem(AUTOMATICALLY_SEND_CLIENT_LOGS, enabled);
  }
};
export var getAutomaticallySendClientLogs = function getAutomaticallySendClientLogs() {
  if (browserSupportStorage() && wrappedLocalStorage.isSet(AUTOMATICALLY_SEND_CLIENT_LOGS)) {
    return JSON.parse(wrappedLocalStorage.getItem(AUTOMATICALLY_SEND_CLIENT_LOGS));
  }
  return true;
};
export var saveApplicationLanguage = function saveApplicationLanguage(language) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem('applicationLanguage', language);
  }
};
export var translateNumber = function translateNumber(number) {
  var lang = document.documentElement.lang;
  if (lang === 'ar') {
    return number.toLocaleString('ar-u-nu-arab');
  }
  return number.toLocaleString(document.documentElement.lang);
};
export var translateTime = function translateTime(number) {
  var lang = document.documentElement.lang;
  if (lang.toLowerCase() === 'ar') {
    return number.replace(/\d/g, function (d) {
      return '٠١٢٣٤٥٦٧٨٩'[d];
    });
  }
  return number;
};
export var addLanguageTohtml = function addLanguageTohtml(language) {
  document.documentElement.lang = language;
};
export var setLanguageDirection = function setLanguageDirection(locale) {
  if (locale !== null && locale !== void 0 && locale.direction) {
    document.documentElement.dir = locale.direction;
  }
};
export var getApplicationLanguage = function getApplicationLanguage() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet('applicationLanguage')) {
    return getTelavoxFormattedLanguageCode(window.navigator.language);
  }
  return wrappedLocalStorage.getItem('applicationLanguage');
};
export var saveSpokenLanguage = function saveSpokenLanguage(language) {
  if (browserSupportStorage()) {
    wrappedLocalStorage.setItem('spokenLanguage', language);
  }
};
export var getSpokenLanguage = function getSpokenLanguage() {
  if (!browserSupportStorage()) {
    return window.navigator.language && window.navigator.language.split('-')[0];
  }
  return wrappedLocalStorage.isSet('spokenLanguage') ? wrappedLocalStorage.getItem('spokenLanguage') : null;
};
export var getSignalIosMenuFinishedLoading = function getSignalIosMenuFinishedLoading() {
  if (!browserSupportStorage()) {
    return false;
  }
  return wrappedLocalStorage.isSet('signalIosMenuFinishedLoading');
};
export var saveCallMethod = function saveCallMethod(method) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(CALL_METHOD, method);
};
export var getCallMethod = function getCallMethod() {
  return browserSupportStorage() && wrappedLocalStorage.isSet(CALL_METHOD) ? wrappedLocalStorage.getItem(CALL_METHOD) : null;
};

/**
 * Forget the stored call method, putting the user back on the same footing as one who has never
 * chosen. Distinct from saving a method: there is no stored value meaning "unchosen", and the
 * default in fetchCompanionDevices keys off its absence.
 */
export var clearCallMethod = function clearCallMethod() {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.removeItem(CALL_METHOD);
};
export var savePreferredCallCanvasSize = function savePreferredCallCanvasSize(size) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(CALL_CANVAS_SIZE, size);
};
export var getPreferredCallCanvasSize = function getPreferredCallCanvasSize() {
  return browserSupportStorage() && wrappedLocalStorage.isSet(CALL_CANVAS_SIZE) ? wrappedLocalStorage.getItem(CALL_CANVAS_SIZE) : DisplayMode.THUMBNAIL.key;
};
export var saveAutoAnswerWithIpPhone = function saveAutoAnswerWithIpPhone(autoAnswer) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(AUTOANSWER_IP_PHONE, autoAnswer);
};
export var getAutoAnswerWithIpPhone = function getAutoAnswerWithIpPhone() {
  if (browserSupportStorage() && wrappedLocalStorage.isSet(AUTOANSWER_IP_PHONE)) {
    return JSON.parse(wrappedLocalStorage.getItem(AUTOANSWER_IP_PHONE));
  }
  return false;
};
export var getAdminLocationFromStorage = function getAdminLocationFromStorage() {
  if (!browserSupportStorage()) {
    return '';
  }
  if (wrappedLocalStorage.isSet(ADMIN_LOCATION)) {
    return JSON.parse(wrappedLocalStorage.getItem(ADMIN_LOCATION));
  }
  return '';
};

/**
 * @param {string} The location at which we find the admin pages
 */
export var setAdminLocationToStorage = function setAdminLocationToStorage(adminLocation) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(ADMIN_LOCATION, JSON.stringify(adminLocation));
};
export var clearAdminLocationFromStorage = function clearAdminLocationFromStorage() {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.removeItem(ADMIN_LOCATION);
};
var filterStateToStore = function filterStateToStore(state) {
  var queueMembers = state.pbx.get('queueMembers', Map());
  return {
    pbx: state.pbx.filter(keyIn('queues', 'refers', 'conferences', 'connections')).update('queues', Map(), function (queues) {
      // Merge queueMembers back into each queue for backward compatibility
      // with base branch that expects queueMembers nested inside queue objects.
      // This so that if a revert was to be made, or a swap to an older branch is made during development,
      // it would still work without typeError in the reducer when filtering queueMembers.
      return queues.map(function (queue) {
        var queueKey = queue.get('key');
        var members = queueMembers.get(queueKey, List());
        return queue.filter(keyIn('key', 'description', 'number')).set('queueMembers', members);
      });
    }).update('voicemails', Map(), function (voicemails) {
      return voicemails.map(function (voicemail) {
        return voicemail.filter(keyIn('key', 'description', 'number', 'settings'));
      });
    }),
    extensions: state.extensions.filter(keyIn('extensions')).update('extensions', function (extensions) {
      return extensions.map(function (extension) {
        return extension.filter(keyIn('key', 'contact', 'admin', 'chatUserKey'));
      });
    })
  };
};
export var saveReduxState = function saveReduxState(state) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(REDUX_STATE, compressToUTF16(JSON.stringify(filterStateToStore(state))));
};
var getReduxStateDeprecated = function getReduxStateDeprecated() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(REDUX_STATE_DEPRECATED)) {
    return undefined;
  }
  var serializedState = wrappedLocalStorage.getItem(REDUX_STATE_DEPRECATED);

  // We have changed the key under which we store the redux state in localStorage from
  // reduxState to reduxStateUTF16 since we are using compressToUTF16 instead of compress
  // (both from lz-string) to compress the data, to make it work in IE, Edge and Firefox.
  // Because of this there might be old redux state data storead under the old key and
  // if there is we have to remove that.
  wrappedLocalStorage.removeItem(REDUX_STATE_DEPRECATED);
  try {
    if (serializedState === null) {
      return undefined;
    }
    var data = JSON.parse(decompress(serializedState));
    var rootObject = {};
    for (var key in data) {
      rootObject[key] = fromJS(data[key]);
    }
    return rootObject;
  } catch (error) {
    console.error('Failed to deserialize cache, error ' + error);
    return undefined;
  }
};

/**
 * Migrate pbx state from old format (queueMembers nested in queues) to new format (queueMembers at top level).
 * This ensures backward compatibility when loading state saved by the base branch.
 */
var migratePbxState = function migratePbxState(pbxState) {
  var _pbxState$get;
  if (!pbxState) return pbxState;

  // If queueMembers already exists at top level, no migration needed
  if (pbxState.has('queueMembers') && ((_pbxState$get = pbxState.get('queueMembers')) === null || _pbxState$get === void 0 ? void 0 : _pbxState$get.size) > 0) {
    return pbxState;
  }
  var queues = pbxState.get('queues', Map());
  var queueMembers = Map();

  // Extract queueMembers from each queue and build top-level queueMembers map
  var migratedQueues = queues.map(function (queue) {
    var queueKey = queue.get('key');
    var members = queue.get('queueMembers');
    if (members) {
      queueMembers = queueMembers.set(queueKey, members);
    }
    return queue["delete"]('queueMembers');
  });
  return pbxState.set('queues', migratedQueues).set('queueMembers', queueMembers);
};
export var getReduxState = function getReduxState() {
  var stateFromDeprecatedKey = getReduxStateDeprecated();
  if (stateFromDeprecatedKey) {
    return stateFromDeprecatedKey;
  }
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(REDUX_STATE)) {
    return undefined;
  }
  var serializedState = wrappedLocalStorage.getItem(REDUX_STATE);
  try {
    if (serializedState === null) {
      return undefined;
    }
    var data = JSON.parse(decompressFromUTF16(serializedState));
    var rootObject = {};
    for (var key in data) {
      rootObject[key] = fromJS(data[key]);
    }

    // Migrate pbx state to handle queueMembers in old format
    if (rootObject.pbx) {
      rootObject.pbx = migratePbxState(rootObject.pbx);
    }
    return rootObject;
  } catch (error) {
    console.error('Failed to deserialize cache, error ' + error);
    return undefined;
  }
};
export var putAppInFocus = function putAppInFocus() {
  var electronRemote = isElectron() ? require('@electron/remote') : null;
  var osIsWindows = electronRemote && electronRemote.process.platform === 'win32';
  if (electronRemote && osIsWindows) {
    if (!electronRemote.getCurrentWindow()) {
      return;
    }

    /**
     * Workaround to focus electron app on Windows.
     */

    electronRemote.getCurrentWindow().show();
  } else {
    window.focus();
    sendExtensionNotification();
  }
};

/**
 * From a string on the form '(?)name1=value1&name2=value2...nameN=valueN',
 * find the value belonging to a certain name.
 *
 * The string may or may not start with a '?' character.
 *
 * @param {String} queryString  A query string
 * @param {String} paramName    A name for one of the parameters
 */
export function getQueryParam(queryString, paramName) {
  if (!queryString || !paramName) {
    return;
  }
  var prunedQueryString = queryString.startsWith('?') ? queryString.substr(1) : queryString;
  var nameValuePairs = prunedQueryString.split('&');
  for (var i = 0; i < nameValuePairs.length; i++) {
    var nameAndValue = nameValuePairs[i].split('=');
    if (!nameAndValue[1]) {
      // This is needed, since if nameAndValue[1] is undefined,
      // decodeURIComponent(nameAndValue[1]) will return a String 'undefined'.
      return;
    }
    if (decodeURIComponent(nameAndValue[0]) === paramName) {
      return decodeURIComponent(nameAndValue[1]); // eslint-disable-line consistent-return
    }
  }
}
export var errorMessageIsPlayRequestInterrupted = function errorMessageIsPlayRequestInterrupted(error) {
  if (!error || !error.message) {
    return false;
  }
  return error.message.includes('The play() request was interrupted by a call to pause()');
};
export var errorMessageIsInteractBeforePlay = function errorMessageIsInteractBeforePlay(error) {
  if (!error || !error.message) {
    return false;
  }
  return error.message.includes("play() failed because the user didn't interact with the document first.");
};
export var errorMessageIsRTCPeerConnectionGone = function errorMessageIsRTCPeerConnectionGone(error) {
  if (!error || !error.message) {
    return false;
  }
  return error.message.includes('RTCPeerConnection is gone');
};
export var setAutoLogin = function setAutoLogin(autoLogin) {
  wrappedLocalStorage.setItem('autoLogin', JSON.stringify(!!autoLogin));
};
export var getAutoLogin = function getAutoLogin() {
  return !!JSON.parse(wrappedLocalStorage.getItem('autoLogin'));
};

/**
 * Check user microphone access in Chrome
 */
export var checkBrowserMicrophonePermission = /*#__PURE__*/function () {
  var _ref5 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    var _window, navigator, microphonePermission, _t2;
    return _regeneratorRuntime.wrap(function (_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          _window = window, navigator = _window.navigator;
          _context3.next = 1;
          return navigator.permissions.query({
            name: MICROPHONE
          });
        case 1:
          microphonePermission = _context3.sent;
          if (!(microphonePermission.state === permissionTypes.PROMPT)) {
            _context3.next = 6;
            break;
          }
          _context3.prev = 2;
          _context3.next = 3;
          return probeMediaPermission({
            audio: true
          });
        case 3:
          _context3.next = 5;
          break;
        case 4:
          _context3.prev = 4;
          _t2 = _context3["catch"](2);
          return _context3.abrupt("return", false);
        case 5:
          _context3.next = 7;
          break;
        case 6:
          if (!(microphonePermission.state === permissionTypes.DENIED)) {
            _context3.next = 7;
            break;
          }
          return _context3.abrupt("return", false);
        case 7:
          return _context3.abrupt("return", true);
        case 8:
        case "end":
          return _context3.stop();
      }
    }, _callee3, null, [[2, 4]]);
  }));
  return function checkBrowserMicrophonePermission() {
    return _ref5.apply(this, arguments);
  };
}();
export var saveChatShortcuts = function saveChatShortcuts(shortCutOn) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(CHAT_SHORTCUTS, JSON.stringify(!!shortCutOn));
};
export var getChatShortcuts = function getChatShortcuts() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(CHAT_SHORTCUTS)) {
    return true;
  }
  return wrappedLocalStorage.getItem(CHAT_SHORTCUTS) === 'true';
};
export var saveUseAsCompanion = function saveUseAsCompanion(useAsCompanion) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(USE_AS_COMPANION, JSON.stringify(!!useAsCompanion));
};

/**
 * Whether the user has designated this desktop install as their companion. Defaults to false:
 * an install that has never been opted in must keep behaving exactly as it did before.
 */
export var getUseAsCompanion = function getUseAsCompanion() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(USE_AS_COMPANION)) {
    return false;
  }
  return wrappedLocalStorage.getItem(USE_AS_COMPANION) === 'true';
};

/**
 * Whether the user deliberately turned this install off as a companion, as opposed to never having
 * turned it on. Both read as false through getUseAsCompanion, and the difference decides whether a
 * boot may promote this machine back to the role its account still designates - see
 * syncDesignation. Writing false where clearUseAsCompanion is meant would erase that difference.
 */
export var hasUseAsCompanionOptOut = function hasUseAsCompanionOptOut() {
  return browserSupportStorage() && wrappedLocalStorage.isSet(USE_AS_COMPANION) && wrappedLocalStorage.getItem(USE_AS_COMPANION) !== 'true';
};

/**
 * Stand this install down without recording it as a choice, leaving it as it was before anyone
 * turned it on. For a stand-down the client decided on - the account designates nothing, or another
 * machine - as opposed to the user saying no, which is saveUseAsCompanion(false).
 */
export var clearUseAsCompanion = function clearUseAsCompanion() {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.removeItem(USE_AS_COMPANION);
};

/**
 * Whether this surface hands its calls to the desktop app when one is available.
 *
 * Defaults to on, so handover works without setup - the user should not have to find a setting
 * before their calls land where they are sitting. Turning it off makes the surface own its own
 * media again and behave exactly as it did before the companion existed, which is why the ordinary
 * call methods stay available alongside it rather than being replaced by it.
 */
export var getHandoverToDesktop = function getHandoverToDesktop() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(USE_COMPANION_FOR_CALLS)) {
    return true;
  }
  return wrappedLocalStorage.getItem(USE_COMPANION_FOR_CALLS) === 'true';
};
export var saveHandoverToDesktop = function saveHandoverToDesktop(handover) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(USE_COMPANION_FOR_CALLS, JSON.stringify(!!handover));
};
export var saveVideoConferenceWithSip = function saveVideoConferenceWithSip(videoConferenceWithSip) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(VIDEO_CONFERENCE_WITH_SIP, JSON.stringify(!!videoConferenceWithSip));
};
export var getVideoConferenceWithSip = function getVideoConferenceWithSip() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(VIDEO_CONFERENCE_WITH_SIP)) {
    return false;
  }
  return wrappedLocalStorage.getItem(VIDEO_CONFERENCE_WITH_SIP) === 'true';
};
export var saveShowLocalVideoInGrid = function saveShowLocalVideoInGrid(showLocalVideoInGrid) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(SHOW_LOCAL_VIDEO_IN_GRID, showLocalVideoInGrid);
};
export var getShowLocalVideoInGrid = function getShowLocalVideoInGrid() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(SHOW_LOCAL_VIDEO_IN_GRID)) {
    return true;
  }
  return wrappedLocalStorage.getItem(SHOW_LOCAL_VIDEO_IN_GRID) === 'true';
};
export var saveShowIncomingVideo = function saveShowIncomingVideo(showIncomingVideo) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(SHOW_INCOMING_VIDEO, showIncomingVideo);
};
export var getShowIncomingVideo = function getShowIncomingVideo() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(SHOW_INCOMING_VIDEO)) {
    return true;
  }
  return wrappedLocalStorage.getItem(SHOW_INCOMING_VIDEO) === 'true';
};
export var saveVideoBackgroundPreferences = function saveVideoBackgroundPreferences(preferences) {
  if (!browserSupportStorage()) {
    return;
  }
  wrappedLocalStorage.setItem(VIDEO_BACKGROUND_PREFERENCES, JSON.stringify(preferences));
};
export var getVideoBackgroundPreferences = function getVideoBackgroundPreferences() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(VIDEO_BACKGROUND_PREFERENCES)) {
    return null;
  }
  try {
    return JSON.parse(wrappedLocalStorage.getItem(VIDEO_BACKGROUND_PREFERENCES));
  } catch (_error) {
    return null;
  }
};
export var hasCallWaitingLocalStorage = function hasCallWaitingLocalStorage() {
  if (!browserSupportStorage()) {
    return false;
  }
  return wrappedLocalStorage.isSet(CALL_WAITING_ENABLED);
};
export var getCallWaitingEnabledFromLocalStorage = function getCallWaitingEnabledFromLocalStorage() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(CALL_WAITING_ENABLED)) {
    return false;
  }
  return wrappedLocalStorage.getItem(CALL_WAITING_ENABLED) === 'true';
};
export var clearCallWaitingFromLocalStorage = function clearCallWaitingFromLocalStorage() {
  if (!browserSupportStorage() || !wrappedLocalStorage.isSet(CALL_WAITING_ENABLED)) {
    return false;
  }
  return wrappedLocalStorage.removeItem(CALL_WAITING_ENABLED) === 'true';
};
export var saveCentralSwitchWidgets = function saveCentralSwitchWidgets(widgetList) {
  if (!browserSupportStorage() || !widgetList) {
    return;
  }
  wrappedLocalStorage.setItem(CENTRAL_SWITCH_WIDGETS, JSON.stringify(widgetList));
};
export var getCentralSwitchWidgets = function getCentralSwitchWidgets() {
  var DefaultWidgets = Object.freeze(['dialPad', 'favoritePBX', 'RecentContacted', 'userCalendar']);
  if (!browserSupportStorage()) {
    return DefaultWidgets;
  } else {
    return wrappedLocalStorage.isSet(CENTRAL_SWITCH_WIDGETS) ? JSON.parse(wrappedLocalStorage.getItem(CENTRAL_SWITCH_WIDGETS)) : DefaultWidgets;
  }
};
export var saveBlindTransferSettings = function saveBlindTransferSettings(setting, value) {
  if (!browserSupportStorage()) {
    return;
  }
  var blindTransferSettings = wrappedLocalStorage.getItem(BLIND_TRANSFER_SETTINGS) ? JSON.parse(wrappedLocalStorage.getItem(BLIND_TRANSFER_SETTINGS)) : {};
  blindTransferSettings[setting] = value;
  wrappedLocalStorage.setItem(BLIND_TRANSFER_SETTINGS, JSON.stringify(blindTransferSettings));
};
export var getBlindTransferSetting = function getBlindTransferSetting(setting) {
  if (!browserSupportStorage()) {
    return null;
  }
  var blindTransferSettings = wrappedLocalStorage.getItem(BLIND_TRANSFER_SETTINGS) ? JSON.parse(wrappedLocalStorage.getItem(BLIND_TRANSFER_SETTINGS)) : {};
  return blindTransferSettings[setting];
};

/**
 * Sends a message to the browser extension handling tab navigation
 * in the case the webapp has been iframed as navigating to a tab
 * through the notification popping up does not work otherwise
 * due to CORS.
 */
var sendExtensionNotification = function sendExtensionNotification() {
  if (window.chrome && window.chrome.runtime) {
    window.chrome.runtime.sendMessage('bdoamjmdnbhgodgonekmefjneeohapkj', {
      type: 'FOCUS_ME'
    }, {}, function () {});
  }
};
export var parseUrlParameter = function parseUrlParameter(parameter) {
  var queryString = window.location.search;
  var urlParams = new URLSearchParams(queryString);
  return urlParams.get(parameter);
};
export var addSipConnectionEstablishedDiv = function addSipConnectionEstablishedDiv() {
  var div = document.createElement('div');
  div.style.display = 'none';
  div.setAttribute('data-test-id', 'sip-connection-established');
  document.body.appendChild(div);
};